// Abdo essaidi : creation du fichier src/contexts/BulkUpdateContext.js

import React, { createContext, useState, useContext, useCallback, useRef } from 'react';
import { customerApi } from '../api/customerApi';

const BulkUpdateContext = createContext(null);

export const BulkUpdateProvider = ({ children }) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [totalToUpdate, setTotalToUpdate] = useState(0);
  const [processedCount, setProcessedCount] = useState(0);
  const [results, setResults] = useState(null); // Abdo essaidi : Pour stocker les messages de succès/erreur persistants
  
  // Abdo essaidi : Ref pour pouvoir accéder à l'état actuel dans les boucles asynchrones si besoin
  const isUpdatingRef = useRef(false);

  const startBulkUpdate = useCallback(async (accessToken) => {
    if (isUpdatingRef.current) return;
    
    setIsUpdating(true);
    isUpdatingRef.current = true;
    setProgress(0);
    setResults(null);
    setProcessedCount(0);

    try {
      // Abdo essaidi : 1. Obtenir le nombre total d'apprenants à mettre à jour
      const countData = await customerApi.getRppsUpdateCount(accessToken);
      const total = countData.count || 0;
      setTotalToUpdate(total);

      if (total === 0) {
        setResults({ status: 'info', message: 'Aucun apprenant à mettre à jour.' });
        setIsUpdating(false);
        isUpdatingRef.current = false;
        return;
      }

      // Abdo essaid : 2. Traiter par lots (Batching)
      const BATCH_SIZE = 20; // Abdo essaidi : Traiter 20 par 20 pour éviter le timeout
      let currentProcessed = 0;
      let errors = [];
      let updatedCount = 0;

      while (currentProcessed < total) {
        // Abdo essaidi : Si l'utilisateur quitte l'app ou refresh, ça coupe, mais la navigation interne React est OK.
        try {
          const result = await customerApi.processRppsBatch(accessToken, BATCH_SIZE, currentProcessed);
          
          updatedCount += result.updated || 0;
          if (result.errors && result.errors.length > 0) {
            errors = [...errors, ...result.errors];
          }
        } catch (err) {
          console.error("Erreur sur le lot", err);
          errors.push(`Erreur technique sur le lot commençant à ${currentProcessed}`);
        }

        currentProcessed += BATCH_SIZE;
        // Abdo essaidi : On s'assure de ne pas dépasser 100%
        const newProgress = Math.min(Math.round((currentProcessed / total) * 100), 100);
        
        setProcessedCount(Math.min(currentProcessed, total));
        setProgress(newProgress);
      }

      // Abdo essaidi : 3. Fin du traitement
      setResults({
        status: errors.length > 0 ? 'warning' : 'success',
        message: `Mise à jour terminée. ${updatedCount} mis à jour.`,
        details: errors.length > 0 ? errors : null
      });

    } catch (error) {
      setResults({
        status: 'error',
        message: 'Une erreur critique est survenue lors de l\'initialisation.',
        details: [error.message]
      });
    } finally {
      setIsUpdating(false);
      isUpdatingRef.current = false;
    }
  }, []);

  const clearResults = () => {
    setResults(null);
    setProgress(0);
    setProcessedCount(0);
    setTotalToUpdate(0);
  };

  return (
    <BulkUpdateContext.Provider value={{ 
      isUpdating, 
      progress, 
      totalToUpdate, 
      processedCount, 
      results, 
      startBulkUpdate, 
      clearResults 
    }}>
      {children}
    </BulkUpdateContext.Provider>
  );
};

export const useBulkUpdate = () => useContext(BulkUpdateContext);